// Session-cookie authentication utilities for the Parslee admin dashboard.
// Login is delegated to the platform (m365dotnet). Unlike SalesTrainer — which
// proxies /connect/* through its own .NET backend — the admin dashboard is a
// static SPA, so it reaches m365's /connect/* endpoints SAME-ORIGIN via the
// vite dev proxy (local) and the nginx /connect/ reverse-proxy (prod) added in
// U3. That is why API_HOST resolves to "" (same origin) in the normal case.

import { resolveApiHostForBrowser } from './api-host';
import { PARSLEE_API_URL } from '../auth/parslee-sso/config';
import {
  validateReturnUrl,
  OneShotBridge,
  type SessionAccount,
  type SessionOrganization,
  type LinkedAccount,
  type SessionAuthStatus,
  type ProbeResult,
} from '@mayline-ai/platform-auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
//
// The session account / organization / linked-account / auth-status shapes are
// the frozen contract types imported from @mayline-ai/platform-auth, not
// redefined here. `SessionResponse` is the app-local normalized view
// `getSession()` returns; it is built FROM those SDK types. Re-export the SDK
// types so existing in-app imports keep resolving.

export type { SessionAccount, SessionOrganization, LinkedAccount, SessionAuthStatus, ProbeResult };
export { OneShotBridge };

export interface SessionResponse {
  authenticated: boolean;
  account?: SessionAccount;
  activeOrganization?: string;
  role?: string;
  organizations?: SessionOrganization[];
  linkedAccounts?: LinkedAccount[];
  entitledProducts?: string[];
  authStatus?: SessionAuthStatus;
  csrfToken?: string;
  /**
   * Whether the caller is a Parslee staffer. Sourced from m365 via the
   * /connect/session response. Defaults to false when absent (old backend /
   * non-staff) — fail-closed. Gates the whole admin dashboard via StaffGate.
   */
  isParsleeStaff?: boolean;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/**
 * API host from environment. Empty string in local dev (vite proxy) and in the
 * normal deployed case (nginx same-origin /connect proxy); set to a backend
 * FQDN only if VITE_API_URL points at a non-loopback cross-origin host.
 */
const API_HOST = resolveApiHostForBrowser(import.meta.env.VITE_API_URL, import.meta.env.DEV);
let platformCsrfToken = '';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export const browserNavigation = {
  reload(): void {
    window.location.reload();
  },
};

/**
 * Build a return URL suitable for OAuth redirects.
 * When API_HOST is set (cross-domain), returns an absolute URL on the frontend
 * origin. When empty (same domain / vite proxy), returns just the path.
 */
export function buildReturnUrl(path: string): string {
  if (API_HOST) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

/**
 * Get the current browser path to return to after login.
 *
 * We must NOT round-trip back into an OAuth callback path or carry OAuth
 * artifacts (code/state/error) into the next login — those would re-enter /
 * corrupt the handoff. After that app-specific filtering, the generic
 * open-redirect safety (reject `//`, cross-origin, non-relative, bad schemes;
 * normalize to a same-origin relative path) is delegated to the shared SDK
 * `validateReturnUrl`. Anything the SDK rejects falls back to "/".
 */
export function getSafeReturnUrl(): string {
  const pathname = window.location.pathname;
  const search = window.location.search;
  const params = new URLSearchParams(search);

  const isAuthPath =
    pathname === '/auth/callback' || pathname.startsWith('/connect/');
  const hasOAuthArtifacts =
    params.has('code') ||
    params.has('state') ||
    params.has('error') ||
    params.has('error_description');

  if (isAuthPath || hasOAuthArtifacts) {
    return '/';
  }

  // Delegate open-redirect validation to the shared SDK. A rejected URL (e.g. a
  // protocol-relative `//evil.com` path) falls back to the app root.
  const result = validateReturnUrl(`${pathname}${search}`, window.location.origin);
  return result.ok ? result.path : '/';
}

// ---------------------------------------------------------------------------
// Silent platform bootstrap (shared SDK OneShotBridge + redirect-spanning cooldown)
// ---------------------------------------------------------------------------
//
// On an unauthenticated load we do NOT reflexively bounce to /auth/login.
// Instead the SPA renders the sign-in surface and fires ONE silent probe of the
// SHARED platform session (VITE_PARSLEE_API_URL /connect/session). Only a
// positive platform signal — the staffer is already signed into the platform —
// earns a single redirect into the dashboard login flow. A negative/failed
// probe leaves the user on the sign-in surface, so a missing platform session
// can never loop.
//
// TWO complementary guards prevent a login loop:
//   1. The shared SDK `OneShotBridge` (one instance per UnauthenticatedGate
//      mount) authorizes the redirect AT MOST once, and only on a positive
//      probe. It folds every negative/indeterminate probe to a non-authorizing
//      result, so repeated negatives within a single page life can never loop.
//   2. A ~15s sessionStorage cooldown (keyed origin+pathname) stamped at the
//      moment of handoff, BEFORE the full-document `redirectToLogin()`
//      navigation. It survives the navigation that destroys the per-mount
//      bridge, so on the next mount `probePlatformSession()` folds it into
//      `reason: 'cooldown'` and the bridge does NOT re-redirect.

const SILENT_BOOTSTRAP_STORAGE_KEY = 'ad_silent_bootstrap_attempted';
const SILENT_BOOTSTRAP_RETRY_COOLDOWN_MS = 15_000;

/** True on the OAuth handoff paths we must never probe through. */
function isAuthPath(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.location.pathname.startsWith('/connect/') ||
    window.location.pathname.startsWith('/auth/')
  );
}

function getSilentBootstrapAttemptKey(): string {
  if (typeof window === 'undefined') {
    return `${SILENT_BOOTSTRAP_STORAGE_KEY}:/`;
  }
  // The probe is a platform-SESSION check, not a per-URL check. Deliberately
  // exclude `search` so nav/tracking/OAuth querystring params don't mint fresh
  // cooldown buckets. Key on origin + pathname only.
  const { origin, pathname } = window.location;
  return `${SILENT_BOOTSTRAP_STORAGE_KEY}:${origin}${pathname}`;
}

/**
 * Remove every per-URL silent-bootstrap cooldown marker. Called when a real
 * dashboard session is established (or on sign-out) so a future genuine
 * sign-out can re-probe the platform.
 */
export function clearSilentBootstrapMarkers(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(`${SILENT_BOOTSTRAP_STORAGE_KEY}:`)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // sessionStorage unavailable (private mode / quota) — nothing to clear.
  }
}

function hasActiveSilentBootstrapCooldown(attemptKey: string): boolean {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(attemptKey);
  } catch {
    // Unavailable storage fails open: no cooldown (one probe is harmless).
    return false;
  }
  if (!stored) {
    return false;
  }

  const attemptedAt = Number(stored);
  if (!Number.isFinite(attemptedAt) || attemptedAt <= 0) {
    try {
      sessionStorage.removeItem(attemptKey);
    } catch {
      /* ignore */
    }
    return false;
  }

  if (Date.now() - attemptedAt >= SILENT_BOOTSTRAP_RETRY_COOLDOWN_MS) {
    try {
      sessionStorage.removeItem(attemptKey);
    } catch {
      /* ignore */
    }
    return false;
  }

  return true;
}

/**
 * Stamp the per-(origin+pathname) cooldown marker for the CURRENT location.
 * Call at the moment a positive probe authorizes the login handoff — BEFORE the
 * `redirectToLogin()` navigation — so the marker survives it and suppresses a
 * re-probe on the next mount (loop break).
 */
export function markSilentBootstrapAttempt(): void {
  try {
    sessionStorage.setItem(getSilentBootstrapAttemptKey(), String(Date.now()));
  } catch {
    // sessionStorage unavailable — proceed without a durable cooldown.
  }
}

/**
 * Probe the SHARED platform session once. Returns a shared-SDK `ProbeResult`
 * the caller feeds to a `OneShotBridge`:
 *   - on an auth/callback path → negative `auth_path`.
 *   - an active per-(origin+pathname) cooldown → negative `cooldown`.
 *   - a 200 with `authenticated: true` → positive.
 *   - a 200 with `authenticated: false` → negative `expired`.
 *   - non-ok / network error / timeout → negative `unreachable`.
 */
export async function probePlatformSession(): Promise<ProbeResult> {
  if (typeof window === 'undefined' || isAuthPath()) {
    return { authenticated: false, reason: 'auth_path' };
  }

  if (hasActiveSilentBootstrapCooldown(getSilentBootstrapAttemptKey())) {
    return { authenticated: false, reason: 'cooldown' };
  }

  try {
    const response = await fetch(`${PARSLEE_API_URL}/connect/session`, {
      credentials: 'include',
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      return { authenticated: false, reason: 'unreachable' };
    }

    const data = await response.json();
    const authenticated = data.authenticated ?? data.Authenticated;
    if (authenticated === true) {
      return { authenticated: true };
    }
    return { authenticated: false, reason: 'expired' };
  } catch {
    return { authenticated: false, reason: 'unreachable' };
  }
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

/**
 * Fetch the current session from m365 via the same-origin /connect/session
 * proxy (vite dev / nginx prod).
 */
export async function getSession(): Promise<SessionResponse> {
  const response = await fetch(`${API_HOST}/connect/session`, {
    credentials: 'include',
    signal: AbortSignal.timeout(8000),
  });

  if (!response.ok) {
    throw new Error(`Session fetch failed (${response.status})`);
  }

  const data = await response.json();

  // Normalize: server may return 200 with authenticated: false
  const authenticated = data.authenticated ?? data.Authenticated ?? false;
  if (authenticated === false) {
    clearPlatformCsrfToken();
    // A confirmed "not authenticated" answer resets the probe budget: drop any
    // stale cooldown markers so a legitimate platform probe isn't suppressed
    // after a sign-out / session expiry.
    clearSilentBootstrapMarkers();
    return { authenticated: false };
  }

  // A real dashboard session exists — drop any silent-probe cooldown markers
  // so a later genuine sign-out can probe the platform again.
  clearSilentBootstrapMarkers();

  const account = data.account ?? data.Account;
  const rawOrgs = data.organizations ?? data.Organizations ?? [];
  const rawLinked = data.linkedAccounts ?? data.LinkedAccounts ?? [];
  const csrfToken = data.csrfToken ?? data.CsrfToken ?? '';
  setPlatformCsrfToken(csrfToken);

  return {
    authenticated: true,
    account: account
      ? {
          id: account.id ?? account.Id,
          email: account.email ?? account.Email,
          name: account.name ?? account.Name,
        }
      : undefined,
    activeOrganization:
      data.activeOrganization ?? data.ActiveOrganization,
    role: data.role ?? data.Role,
    organizations: rawOrgs.map((o: Record<string, string>) => ({
      id: o.id ?? o.Id,
      name: o.name ?? o.Name,
      role: o.role ?? o.Role,
    })),
    linkedAccounts: rawLinked.map((a: Record<string, string>) => ({
      email: a.email ?? a.Email,
      name: a.name ?? a.Name,
      organizationName:
        a.organizationName ?? a.OrganizationName ?? a.organization_name ?? '',
      organizationId: a.organizationId ?? a.OrganizationId ?? a.organization_id ?? '',
      accountId: a.accountId ?? a.AccountId ?? a.account_id ?? '',
    })),
    entitledProducts:
      data.entitledProducts ?? data.EntitledProducts ?? [],
    authStatus: data.authStatus ?? data.AuthStatus,
    csrfToken,
    // Tolerant mapping: backend proxy emits camelCase isParsleeStaff; tolerate
    // PascalCase too. Anything other than an explicit true is not-staff.
    isParsleeStaff:
      (data.isParsleeStaff ?? data.IsParsleeStaff) === true,
  };
}

// ---------------------------------------------------------------------------
// Login / Logout
// ---------------------------------------------------------------------------

/**
 * Redirect to the same-origin /auth/login route on the dashboard's own login
 * backend (U8), which runs the PKCE OAuth handoff to m365 /connect/authorize and
 * mints the session cookie on the callback. Includes returnUrl so the user lands
 * back where they were. `provider` forwards the chosen IdP ('microsoft' |
 * 'google') so the shared SignInCard's two buttons are functionally distinct;
 * omitting it preserves m365's default.
 */
export function redirectToLogin(
  returnUrl?: string,
  provider?: 'microsoft' | 'google',
): void {
  const safePath = returnUrl ?? getSafeReturnUrl();
  const fullReturnUrl = buildReturnUrl(safePath);
  let url = `${API_HOST}/auth/login?returnUrl=${encodeURIComponent(fullReturnUrl)}`;
  if (provider) {
    url += `&provider=${encodeURIComponent(provider)}`;
  }
  window.location.href = url;
}

/**
 * Redirect to add a new linked account — the same /auth/login handoff with
 * mode=add_account, which the backend forwards to m365 (forces a fresh login).
 */
export function redirectToAddAccount(): void {
  const fullReturnUrl = buildReturnUrl(getSafeReturnUrl());
  window.location.href = `${API_HOST}/auth/login?returnUrl=${encodeURIComponent(fullReturnUrl)}&mode=add_account`;
}

/**
 * Switch to a linked account via m365's /connect/switch-session handoff (proxied
 * same-origin). When the identifier looks like an account id (`acc_...`) send it
 * as `accountId` (m365's O(1) membership check); otherwise send it as `email`.
 * `client_id` + `redirect_uri` let m365 resolve THIS app's origin for the return
 * redirect (`redirect` is the relative return path).
 */
export function switchToAccount(identifier: string): void {
  const returnPath = getSafeReturnUrl();
  const params = new URLSearchParams();
  if (identifier.startsWith('acc_')) {
    params.set('accountId', identifier);
  } else {
    params.set('email', identifier);
  }
  params.set('redirect', returnPath);
  params.set('client_id', 'parslee-admin-dashboard');
  params.set('redirect_uri', `${window.location.origin}/auth/callback`);
  window.location.href = `${API_HOST}/connect/switch-session?${params.toString()}`;
}

/**
 * Switch the active organization through the /connect/switch-org proxy.
 */
export async function switchOrganization(orgId: string): Promise<void> {
  const csrf = getCsrfToken();
  const response = await fetch(`${API_HOST}/connect/switch-org`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
    },
    body: JSON.stringify({ organizationId: orgId }),
  });

  if (!response.ok) {
    const text = await response.text();
    let detail = text;
    try {
      const problem = JSON.parse(text);
      detail = problem.detail ?? problem.message ?? text;
    } catch {
      // Keep the raw response text.
    }
    throw new Error(detail || `Organization switch failed (${response.status})`);
  }

  browserNavigation.reload();
}

/**
 * Log out by POSTing to /connect/logout with the CSRF token, then redirect the
 * browser to "/" (the single surface that runs the silent platform probe).
 */
export async function logout(): Promise<void> {
  const csrf = getCsrfToken();
  clearPlatformCsrfToken();
  // Reset the probe budget before navigating: a fresh sign-out should be able to
  // re-probe the platform session.
  clearSilentBootstrapMarkers();
  try {
    await fetch(`${API_HOST}/connect/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { 'X-CSRF-Token': csrf } : {},
    });
  } catch {
    // Best-effort — redirect regardless
  }
  window.location.href = '/';
}

// ---------------------------------------------------------------------------
// CSRF
// ---------------------------------------------------------------------------

/**
 * Read the CSRF token to send as the X-CSRF-Token header on state-changing
 * requests. Prefers the in-memory session token returned by /connect/session
 * (works cross-host); falls back to the parslee_csrf cookie for
 * same-origin/local-dev deployments where the cookie is readable.
 */
export function getCsrfToken(): string {
  // Prefer the in-memory session token (works cross-host, from /connect/session).
  const inMemory = getPlatformCsrfToken();
  if (inMemory) return inMemory;
  // Fallback: same-origin cookie read (local dev / same-host deploys).
  const match = document.cookie.match(/(?:^|;\s*)parslee_csrf=([^;]*)/);
  return match?.[1] || platformCsrfToken || '';
}

/**
 * Read the platform session-derived CSRF token returned by /connect/session.
 */
export function getPlatformCsrfToken(): string {
  return platformCsrfToken;
}

export function setPlatformCsrfToken(token: string | null | undefined): void {
  platformCsrfToken = token ?? '';
}

export function clearPlatformCsrfToken(): void {
  platformCsrfToken = '';
}
